import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { FEATURE_DEFINITIONS } from '../../../config/planFeatures';

const FeatureMatrixCard = ({ pricingTiers = [] }) => {
  // Build a unified, stable list across plans. New editor rows use `key`;
  // legacy text-only rows remain readable until they are edited and saved.
  const allFeatures = useMemo(() => {
    const featureMap = new Map(
      FEATURE_DEFINITIONS.map((feature) => [feature.key, feature.label])
    );
    pricingTiers?.forEach(tier => {
      if (Array.isArray(tier?.features)) {
        tier?.features?.forEach(f => {
          const key = f?.key || f?.featureKey || f?.text || f?.name;
          if (key) featureMap.set(key, f?.text || f?.name || key);
        });
      }
      Object.keys(tier?.featureEntitlements || {}).forEach((key) => {
        if (!featureMap.has(key)) featureMap.set(key, key.replace(/_/g, ' '));
      });
    });
    return Array.from(featureMap, ([key, label]) => ({ key, label }));
  }, [pricingTiers]);

  const getFeatureValue = (tier, featureKey) => {
    const configured = tier?.featureEntitlements?.[featureKey];
    if (configured && typeof configured === 'object') return configured;
    const match = Array.isArray(tier?.features) && tier?.features?.find(f =>
      (f?.key || f?.featureKey || f?.text || f?.name) === featureKey
    );
    if (match) return { included: match?.included !== false, value: match?.value };

    const catalogFeature = FEATURE_DEFINITIONS.find((feature) => feature.key === featureKey);
    const catalogValue = catalogFeature?.plans?.[tier?.planKey];
    if (catalogValue === undefined) return null;
    return {
      included: catalogValue !== false,
      value: typeof catalogValue === 'string' ? catalogValue : '',
    };
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return <Icon name="Minus" size={16} className="text-muted-foreground/40 mx-auto" />;
    }
    if (typeof value === 'object') {
      if (value.included === false) return <Icon name="X" size={18} className="text-muted-foreground/50 mx-auto" />;
      if (value.value) return <span className="text-sm text-foreground font-medium">{value.value}</span>;
      return <Icon name="Check" size={18} className="text-green-600 mx-auto" />;
    }
    const lower = String(value)?.toLowerCase();
    if (lower === 'yes' || lower === 'true' || lower === '✓' || lower === 'included') {
      return <Icon name="Check" size={18} className="text-green-600 mx-auto" />;
    }
    if (lower === 'no' || lower === 'false' || lower === '✗' || lower === 'not included') {
      return <Icon name="X" size={18} className="text-muted-foreground/50 mx-auto" />;
    }
    return <span className="text-sm text-foreground font-medium">{value}</span>;
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Feature Comparison Matrix</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Compare features across all pricing plans
        </p>
      </div>
      {pricingTiers?.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <Icon name="Grid3x3" size={48} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No pricing plans to compare</p>
        </div>
      ) : allFeatures?.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <Icon name="List" size={48} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No features defined on any plan yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Edit a plan and add features to see them here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-foreground min-w-[180px]">Feature</th>
                {pricingTiers?.map(tier => (
                  <th key={tier?.id} className="px-6 py-3 text-center text-sm font-medium text-foreground min-w-[120px]">
                    <div className="flex flex-col items-center gap-1">
                      <span>{tier?.tierName}</span>
                        {tier?.planKey && <span className="text-[10px] font-normal text-muted-foreground">{tier.planKey}</span>}
                      {tier?.isPopular && (
                        <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full">Popular</span>
                      )}
                      {!tier?.isActive && (
                        <span className="px-1.5 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">Inactive</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="hover:bg-muted/30 transition-colors bg-muted/10">
                <td className="px-6 py-3 text-sm font-semibold text-foreground">Annual billing</td>
                {pricingTiers?.map(tier => (
                  <td key={tier?.id} className="px-6 py-3 text-center text-sm">
                    {tier?.annualPrice != null
                      ? <><span className="font-bold text-primary">${tier?.annualMonthlyPrice ?? Number(tier.annualPrice) / 12}/mo</span><span className="block text-xs text-muted-foreground">${tier.annualPrice}/yr</span></>
                      : '—'}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {/* Pricing row */}
              <tr className="hover:bg-muted/30 transition-colors bg-muted/10">
                <td className="px-6 py-3 text-sm font-semibold text-foreground">Monthly Price</td>
                {pricingTiers?.map(tier => (
                  <td key={tier?.id} className="px-6 py-3 text-center">
                    <span className="text-sm font-bold text-primary">
                      {tier?.price !== undefined && tier?.price !== null
                        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })?.format(tier?.price)
                        : '—'}
                    </span>
                  </td>
                ))}
              </tr>
              {/* Limits rows */}
              <tr className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-3 text-sm font-medium text-foreground">Max Forms</td>
                {pricingTiers?.map(tier => (
                  <td key={tier?.id} className="px-6 py-3 text-center">
                    <span className="text-sm text-foreground">
                      {tier?.maxForms === -1 ? 'Unlimited' : (tier?.maxForms ?? '—')}
                    </span>
                  </td>
                ))}
              </tr>
              <tr className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-3 text-sm font-medium text-foreground">Submissions / Month</td>
                {pricingTiers?.map(tier => (
                  <td key={tier?.id} className="px-6 py-3 text-center">
                    <span className="text-sm text-foreground">
                      {tier?.maxSubmissions === -1 ? 'Unlimited' : (tier?.maxSubmissions ?? '—')}
                    </span>
                  </td>
                ))}
              </tr>
              <tr className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-3 text-sm font-medium text-foreground">Team Seats</td>
                {pricingTiers?.map(tier => (
                  <td key={tier?.id} className="px-6 py-3 text-center text-sm text-foreground">
                    {tier?.maxTeamMembers === -1 ? 'Unlimited' : (tier?.maxTeamMembers ?? '—')}
                  </td>
                ))}
              </tr>
              <tr className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-3 text-sm font-medium text-foreground">Storage</td>
                {pricingTiers?.map(tier => (
                  <td key={tier?.id} className="px-6 py-3 text-center text-sm text-foreground">
                    {tier?.maxStorageMb === -1 ? 'Unlimited' : (tier?.maxStorageMb != null ? `${tier.maxStorageMb >= 1024 ? tier.maxStorageMb / 1024 : tier.maxStorageMb} ${tier.maxStorageMb >= 1024 ? 'GB' : 'MB'}` : '—')}
                  </td>
                ))}
              </tr>
              {/* Dynamic feature rows */}
              {allFeatures?.map(feature => (
                <tr key={feature.key} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">{feature.label}</td>
                  {pricingTiers?.map(tier => (
                    <td key={tier?.id} className="px-6 py-4 text-center">
                      {renderValue(getFeatureValue(tier, feature.key))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeatureMatrixCard;
